import React, { createContext, useContext, useEffect, useState } from "react";
import {
  AnonymousProgressContextState,
  useAnonymousProgressContext,
} from "@/hooks/progress-context/use-anonymous-progress-provider";
import { aiMasteringSessionStorage } from "@/services/storage/ai-mastering-session-storage";
import $api from "@/data/api.constants";
import { useAiMastering } from "@/components/pages/ai-mastering/providers/ai-mastering-provider";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";
import { ErrorType } from "@prisma/client";

interface MasteringProgressContextState extends AnonymousProgressContextState {
  masteredTrack: File | undefined;
  clearMasteredTrack: () => void;
  isLoading: boolean;
  resetProgressContext: () => Promise<void>;
}

const MasteringProgressContext = createContext<
  MasteringProgressContextState | undefined
>(undefined);

export const useMasteringProgressContext =
  (): MasteringProgressContextState => {
    const context = useContext(MasteringProgressContext);
    if (!context) {
      throw new Error(
        "useMasteringProgressContext must be used within an MasteringProgressProvider",
      );
    }
    return context;
  };

const MasteringProgressProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const logger = useLoggingContext();

  const { setTargetTrack, setReferenceTrack, setSelectedPreset } =
    useAiMastering();
  const [masteredTrack, setMasteredTrack] = useState<File | undefined>(
    undefined,
  );

  const [isLoading, setIsLoading] = useState(false);

  // load mastered track from local storage
  useEffect(() => {
    setIsLoading(true);
    aiMasteringSessionStorage.aiMasteringStorage
      .getResultAudioData()
      .then((file) => {
        setMasteredTrack(file?.songFile);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const resetProgressContext = async () => {
    setMasteredTrack(undefined);
    await aiMasteringSessionStorage.aiMasteringStorage.setResultAudioData(null);
  };

  const handleCompleteMessage = async (
    message: any,
    setDescription?: () => React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const resultUrl = message.resultUrl;
    const fileName = message.fileName;

    setIsLoading(true);

    try {
      const fileResponse = await fetch(resultUrl);

      if (!fileResponse.ok) {
        await resetProgressContext();
        return;
      }

      const blob = await fileResponse.blob();

      await aiMasteringSessionStorage.aiMasteringStorage.setResultAudioData({
        songFileUrl: resultUrl,
        title: fileName,
      });

      updateMasteredTrack(new File([blob], fileName));
    } catch (err) {
      logger.logError(err as Error);
      await resetProgressContext();
    } finally {
      setIsLoading(false);
    }
  };

  const handleErrorMessage = async (errorType: ErrorType) => {
    await resetProgressContext();
  };
  const updateMasteredTrack = (masteredFile: File) => {
    setMasteredTrack(masteredFile);
    setTargetTrack(undefined);
    setReferenceTrack(undefined);
    setSelectedPreset(undefined);
  };

  const buildWebSocketUrl = (processId: string) => {
    return `${$api.aiMasteringWebsocketServer}mastering/${processId}`;
  };

  const context = useAnonymousProgressContext({
    handleCompleteMessage: handleCompleteMessage,
    buildWebSocketUrl,
    handleErrorMessage,
  });

  const clearMasteredTrack = () => {
    setMasteredTrack(undefined);
  };

  return (
    <MasteringProgressContext.Provider
      value={{
        ...context,
        masteredTrack,
        clearMasteredTrack,
        isLoading,
        resetProgressContext,
      }}
    >
      {children}
    </MasteringProgressContext.Provider>
  );
};

export default MasteringProgressProvider;
